import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Loader2, Star } from "lucide-react";
import { toast } from "sonner";

const API_URL = '';

const CATEGORIES = [
  { value: "eat", label: "Eat" },
  { value: "do", label: "Do" },
  { value: "explore", label: "Explore" },
  { value: "experiences", label: "Experiences" },
  { value: "carhire", label: "Car Hire" },
  { value: "wellness", label: "Wellness" },
  { value: "nightlife", label: "Nightlife" },
  { value: "shopping", label: "Shopping" },
];

export default function AdminBusinessForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "eat",
    is_featured: false,
    phone: "",
    website: "",
    address: "",
    latitude: "",
    longitude: "",
    image_url: "",
    image_credit: "",
    is_active: true,
  });

  useEffect(() => {
    if (isEditing) {
      const fetchBusiness = async () => {
        try {
          const response = await fetch(`${API_URL}/api/admin/businesses/${id}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setFormData({
              name: data.name || "",
              description: data.description || "",
              category: data.category || "eat",
              is_featured: data.is_featured || false,
              phone: data.phone || "",
              website: data.website || "",
              address: data.address || "",
              latitude: data.latitude || "",
              longitude: data.longitude || "",
              image_url: data.image_url || "",
              image_credit: data.image_credit || "",
              is_active: data.is_active !== false,
            });
          }
        } catch (error) {
          console.error('Failed to fetch business:', error);
          toast.error('Failed to load business data');
        } finally {
          setLoading(false);
        }
      };
      fetchBusiness();
    }
  }, [id, isEditing]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = isEditing
        ? `${API_URL}/api/admin/businesses/${id}`
        : `${API_URL}/api/admin/businesses`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(isEditing ? 'Business updated successfully' : 'Business created successfully');
        navigate('/admin/businesses');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save business');
      }
    } catch (error) {
      toast.error('Failed to save business');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-business-form">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <Link 
            to="/admin/businesses"
            className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-700" />
          </Link>
          <h1 className="text-xl font-bold text-slate-900 font-heading">
            {isEditing ? 'Edit Business' : 'Add Business'}
          </h1>
        </div>
      </header>

      {/* Form */}
      <main className="px-6 py-8">
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 font-heading">Basic Information</h2>
            
            <div className="space-y-2">
              <Label htmlFor="name">Business Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter business name"
                required
                className="h-12"
                data-testid="input-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe the business..."
                required
                rows={3}
                data-testid="input-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                <SelectTrigger className="h-12" data-testid="select-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Featured Boost */}
          <div className={`rounded-2xl p-6 border shadow-sm ${
            formData.is_featured 
              ? 'bg-amber-50 border-amber-200' 
              : 'bg-white border-slate-100'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  formData.is_featured ? 'bg-amber-100' : 'bg-slate-100'
                }`}>
                  <Star className={`w-5 h-5 ${
                    formData.is_featured ? 'text-amber-600 fill-amber-600' : 'text-slate-400'
                  }`} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 font-heading">Featured Boost</h2>
                  <p className="text-sm text-slate-500">Featured businesses appear first with a badge</p>
                </div>
              </div>
              <Switch
                checked={formData.is_featured}
                onCheckedChange={(checked) => handleChange('is_featured', checked)}
                data-testid="switch-is-featured"
              />
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 font-heading">Contact Information</h2>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+1-555-0100"
                required
                className="h-12"
                data-testid="input-phone"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Website URL</Label>
              <Input
                id="website"
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://example.com"
                className="h-12"
                data-testid="input-website"
              />
              <p className="text-xs text-slate-500">All businesses can display a website link</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="123 Main Street, City"
                required
                className="h-12"
                data-testid="input-address"
              />
            </div>

            {/* Map Coordinates */}
            <div className="pt-4 border-t border-slate-100">
              <h3 className="text-sm font-medium text-slate-700 mb-3">Map Coordinates (Optional)</h3>
              <p className="text-xs text-slate-500 mb-3">
                For precise map pin placement. Leave blank to use address geocoding.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="latitude">Latitude</Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => handleChange('latitude', e.target.value ? parseFloat(e.target.value) : "")}
                    placeholder="-28.0167"
                    className="h-12"
                    data-testid="input-latitude"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="longitude">Longitude</Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => handleChange('longitude', e.target.value ? parseFloat(e.target.value) : "")}
                    placeholder="153.4000"
                    className="h-12"
                    data-testid="input-longitude"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Tip: Find coordinates at maps.google.com - right-click location → "What's here?"
              </p>
            </div>
          </div>

          {/* Media */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 font-heading">Media</h2>
            
            <div className="space-y-2">
              <Label htmlFor="image_url">Image URL</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => handleChange('image_url', e.target.value)}
                placeholder="https://images.unsplash.com/..."
                className="h-12"
                data-testid="input-image-url"
              />
            </div>

            {formData.image_url && (
              <div className="mt-4">
                <p className="text-sm text-slate-500 mb-2">Preview</p>
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="w-full h-40 object-cover rounded-xl"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="image_credit">Image Credit</Label>
              <Input
                id="image_credit"
                value={formData.image_credit}
                onChange={(e) => handleChange('image_credit', e.target.value)}
                placeholder="e.g., Unsplash, Photographer name"
                className="h-12"
                data-testid="input-image-credit"
              />
              <p className="text-xs text-slate-500">Attribution for the image source</p>
            </div>
          </div>

          {/* Status */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 font-heading">Status</h2>
                <p className="text-sm text-slate-500">Inactive businesses are hidden from guests</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => handleChange('is_active', checked)}
                data-testid="switch-is-active"
              />
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Link to="/admin/businesses" className="flex-1">
              <Button type="button" variant="outline" className="w-full h-12 rounded-full">
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 h-12 rounded-full bg-teal-600 hover:bg-teal-700"
              data-testid="submit-button"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isEditing ? 'Update Business' : 'Create Business'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
